const _ = require('lodash')

const defaultEquality = _.isEqual
const defaultListIdentifier = item => item.id
/**
 * Differ class to compare two lists and detect changes such as added, removed, or modified items.
 * @template {object} T
 */
export default class Differ {
	/**
	 * Constructs a Differ instance.
	 * @param {Array<T>} oldList - The old list of items.
	 * @param {Array<T>} newList - The new list of items.
	 * @param {Function} [equality] - A custom equality function to compare items.
	 * @param {Function} [oldListIdentifier] - A function to identify items in the old list.
	 * @param {Function} [newListIdentifier] - A function to identify items in the new list.
	 */
	constructor(
		oldList = [],
		newList = [],
		equality = defaultEquality,
		oldListIdentifier = defaultListIdentifier,
		newListIdentifier = defaultListIdentifier) {
		if (_.isNil(equality)) {
			equality = _.isEqual
		}
		if (_.isNil(oldListIdentifier)) {
			oldListIdentifier = defaultListIdentifier
		}
		if (_.isNil(newListIdentifier)) {
			newListIdentifier = defaultListIdentifier
		}

		const oldMap = _.keyBy(oldList, oldListIdentifier)

		const hasId = (item) => newListIdentifier(item) !== null
		const newListHasId = _.filter(newList, hasId)
		const newMap = _.keyBy(newListHasId, newListIdentifier)
		const noId = _.reject(newList, hasId)
		/**
		 * @type {Set<Number>}
		 */
		const possiblyUnchangedIds = new Set(_.map(newListHasId, newListIdentifier))

		this.removed = _(oldMap)
			.filter((value, key) => !_.has(newMap, key))
			.map((value, key) => {
				possiblyUnchangedIds.delete(key)
				return value
			})
			.value()

		this.added = _.filter(newMap,
			(value, key) => !_.has(oldMap, key))
		this.added.push(...noId)

		this.modified = []
		this.modifiedPairs = []
		_.forEach(newMap, (newItem, key) => {
			if (_.has(oldMap, key)) {
				const oldItem = oldMap[key]
				if (!equality(oldItem, newItem)) {
					// delete it using the identifier function instead of
					// key here, since key is a String and our ids are Numbers
					possiblyUnchangedIds.delete(oldListIdentifier(oldItem))
					this.modified.push(newItem)
					this.modifiedPairs.push(new OldAndNew(oldItem, newItem))
				}
			}
		})

		this.unchanged = _.filter(oldList, (oldItem) =>
				possiblyUnchangedIds.has(oldListIdentifier(oldItem)))

		this.unchangedPairs = _.map(this.unchanged,
			(oldItem) => new OldAndNew(oldItem,
				newMap[oldListIdentifier(oldItem)]))
	}

	/**
	 * Returns all changes (removed, added, and modified).
	 * @returns {Array<T>}
	 */
	allChanges() {
		return _.flatten([this.removed, this.added, this.modified])
	}

	/**
	 * Returns additive changes (added and modified).
	 * @returns {Array<T>}
	 */
	additiveChanges() {
		return _.flatten([this.added, this.modified])
	}
}

/**
 * Class representing a pair of old and new objects.
 * @template T
 */
class OldAndNew {
	/**
	 * Creates an OldAndNew pair.
	 * @param {T} oldObj - The old object.
	 * @param {T} newObj - The new object.
	 */
	constructor(oldObj, newObj) {
		this.oldObj = oldObj
		this.newObj = newObj
	}
}